/**
 * 부팅/네비게이션 시 서버 세션을 검증한다.
 * - 한 탭에서 한 번만 실행 (serverVerifiedAt로 가드)
 * - user==null이면 cold boot 세션 프로브 1회 (iOS localStorage eviction 복구):
 *   fetchMe 200 → setAuth로 세션 복구 / 실패 → markServerVerified (탭당 재프로브 방지)
 * - fetchMe 401 → clearAuth (세션 무효 확정)
 * - fetchMe 비-401 (네트워크/500 등) → user 유지, markServerVerified만
 *   (오프라인/일시 장애에서 사용자를 강제 로그아웃시키지 않음)
 */
import { isAxiosError } from 'axios';
import { useAuthStore } from '@/shared/model';
import { authApi } from '../api/authApi';

export async function verifyServerSession(): Promise<void> {
  const { user, serverVerifiedAt, setAuth, clearAuth, markServerVerified } =
    useAuthStore.getState();

  if (serverVerifiedAt != null) return;

  if (user == null) {
    // iOS는 ~7일 비활성 시 localStorage를 evict하지만 httpOnly 세션 쿠키는 살아있다.
    // 영속 user가 비어도 cold boot 시 딱 한 번 서버 세션을 확인해 복구한다.
    try {
      const fresh = await authApi.fetchMe();
      setAuth(fresh);
    } catch {
      // 401/네트워크/500 등 — 세션 복구 불가. user는 이미 null이므로 가드가
      // /login으로 보낸다. 탭당 재프로브 방지 위해 markServerVerified.
      markServerVerified();
    }
    return;
  }

  try {
    const fresh = await authApi.fetchMe();
    setAuth(fresh);
  } catch (err) {
    if (isAxiosError(err) && err.response?.status === 401) {
      // 세션 무효 확정 — clearAuth 사용 (logout()은 onLogoutCallback이 navigate를
      // 발화시켜 beforeLoad와 충돌. routes/index.tsx의 redirect가 자연스럽게
      // /login으로 보냄)
      clearAuth();
    } else {
      // 네트워크/500 등 일시 장애 — user 유지하여 graceful degradation
      markServerVerified();
    }
  }
}
